import { LngLat } from '@yandex/ymaps3-types';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import {
  Feature as ClustererFeature,
  IClusterMethod
} from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback, useRef } from 'react';

import { ReactifyApi, Ymaps3Clusterer } from '@/fo/ui/mod/yandex/maps';

import { MapUpdateEvent } from '../../hooks/useMapController';
import { PointFeature } from '../../types';
import { Cluster } from '../Cluster/Cluster';
import { Controls } from '../Controls/Controls';
import { Marker } from '../Marker/Marker';

import { styles } from './MapContent.styles';

export interface MapContentProps {
  reactifyApi: ReactifyApi;
  clusterer: Ymaps3Clusterer;
  gridSizedMethod: IClusterMethod;
  pointsList: PointFeature[];
  selectedPoint: PointFeature | null;
  location: YMapLocationRequest;
  isDesktop: boolean;
  onMarkerClick: (feature: PointFeature) => void;
  onClusterClick: (features: ClustererFeature[]) => void;
  onMapUpdate: (event: MapUpdateEvent) => void;
  selectedCity?: string;
}

/**
 * Компонент для отображения карты с маркерами и кластерами
 */
export function MapContent({
  reactifyApi,
  clusterer,
  gridSizedMethod,
  pointsList,
  selectedPoint,
  location,
  onMarkerClick,
  onClusterClick,
  onMapUpdate,
  selectedCity
}: MapContentProps) {
  const slots = styles();

  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapControls,
    YMapListener
  } = reactifyApi;

  const { YMapClusterer } = clusterer;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);

  const handleZoom = (delta: number) => {
    const map = mapRef.current;
    if (map) {
      map.update({ location: { zoom: map.zoom + delta, duration: 200 } });
    }
  };
  /**
   * Рендер маркера
   */
  const marker = useCallback(
    (feature: ClustererFeature) => (
      <YMapMarker
        coordinates={[
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1]
        ]}
      >
        <Marker
          feature={feature as PointFeature}
          onClick={onMarkerClick}
          isSelected={selectedPoint?.id === feature.id}
          selectedCity={selectedCity}
        />
      </YMapMarker>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [YMapMarker, onMarkerClick, selectedPoint?.id]
  );

  /**
   * Рендер кластера
   */
  const cluster = useCallback(
    (coordinates: LngLat, features: ClustererFeature[]) => (
      <YMapMarker coordinates={coordinates}>
        <Cluster
          features={features}
          onClick={onClusterClick}
        />
      </YMapMarker>
    ),
    [YMapMarker, onClusterClick]
  );

  return (
    <div className={slots.base()}>
      <YMap
        location={location}
        ref={mapRef}
      >
        <YMapDefaultSchemeLayer />
        <YMapListener onUpdate={onMapUpdate} />
        <YMapControls position="right">
          <Controls onClick={handleZoom} />
        </YMapControls>
        <YMapDefaultFeaturesLayer />

        <YMapClusterer
          features={pointsList}
          method={gridSizedMethod}
          marker={marker}
          cluster={cluster}
        />
      </YMap>
    </div>
  );
}
